import type { CountryCode } from "libphonenumber-js";
import { forwardRef, type InputHTMLAttributes, useMemo } from "react";
import { cn } from "../cn";
import { phoneCountryOptions, phonePlaceholder } from "../phone";
import { Combobox, type ComboboxOption } from "./Combobox";

export interface PhoneInputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "value" | "onChange" | "type"
  > {
  /** National-notation number, e.g. `06 12 34 56 78`. */
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly country: CountryCode;
  readonly onCountryChange: (country: CountryCode) => void;
}

/**
 * Phone field split into a country selector and a national-notation number
 * input, rendered as one seamless control group.
 *
 * The country is explicit state rather than being sniffed from what the user
 * types: a bare `06…` is a valid national prefix in several countries, so
 * guessing would silently produce the wrong E.164 number. Keeping the country
 * separate also lets the caller validate against that country's real rules
 * (see `isValidPhoneForCountry`) instead of assuming NL.
 *
 * The selector is the shared `Combobox`, matching the other popover controls
 * in these forms rather than introducing a one-off native `<select>`. Its
 * trigger shows the calling code (`+31`) rather than the country name, since
 * that is what still matters once a choice is made and it keeps the control
 * compact so the number itself gets the space.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      className,
      value,
      onValueChange,
      country,
      onCountryChange,
      disabled,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const options: ComboboxOption[] = useMemo(
      () =>
        phoneCountryOptions.map((option) => ({
          value: option.value,
          // Searchable by country name; the trigger is compacted to just the
          // calling code via `triggerLabel` below.
          label: option.label,
          hint: option.callingCode,
        })),
      [],
    );

    const selected = phoneCountryOptions.find(
      (option) => option.value === country,
    );

    return (
      <div
        className={cn(
          // One control group: the two halves share a single border and focus
          // treatment so it reads as a single field rather than two controls
          // that happen to sit next to each other.
          "flex w-full items-stretch rounded-xl border border-input bg-surface shadow-sm transition-colors",
          "focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15",
          "has-[[aria-invalid=true]]:border-destructive",
          disabled && "pointer-events-none opacity-50",
          className,
        )}
      >
        <Combobox
          aria-label="Landcode"
          value={country}
          onValueChange={(next) => next && onCountryChange(next as CountryCode)}
          options={options}
          searchPlaceholder="Zoek land"
          emptyText="Geen land gevonden"
          triggerLabel={selected?.callingCode ?? country}
          clearable={false}
          disabled={disabled}
          // The group owns the chrome, so the trigger renders bare. Note the
          // split: `className` styles the Combobox's wrapper, while
          // `triggerClassName` is the only way to reach the button itself.
          // The wrapper must stretch too, otherwise the button's `h-full` has
          // nothing to fill and the chevron centres against the wrong height.
          className="shrink-0 self-stretch"
          triggerClassName={cn(
            // The trigger only ever shows a short dial code ("+31"), so drop
            // the Combobox's default 10rem min-width floor — that's sized for
            // full-text options. A small floor of its own keeps the three
            // codes (+31/+32/+49) from resizing the field as you switch, and
            // leaves the chevron breathing room instead of crowding the code.
            "min-w-[5.5rem] pl-3 pr-8",
            // The group stretches its children; the trigger's own fixed `h-10`
            // would leave it shorter than the row, so the absolutely-centred
            // chevron would sit off-centre against the code. Match the row.
            "h-full self-stretch",
            "rounded-l-xl rounded-r-none border-0 bg-transparent shadow-none",
            "hover:bg-muted/40 focus-visible:ring-0",
          )}
        />
        <span
          aria-hidden="true"
          className="my-2 w-px shrink-0 self-stretch bg-border"
        />
        <input
          ref={ref}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          disabled={disabled}
          // Defaults to a real example number for the selected country, so the
          // hint tracks the country instead of being a fixed NL-shaped string.
          placeholder={placeholder ?? phonePlaceholder(country)}
          className="h-10 min-w-0 flex-1 rounded-r-xl bg-transparent px-3 text-sm text-foreground outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground"
          {...props}
        />
      </div>
    );
  },
);
PhoneInput.displayName = "PhoneInput";
